import React, { useContext } from 'react';
import LabelExpandContext from '../PaginatedSelect/LabelExpandContext';

/**
 * The one place that decides how a property is shown inside a dropdown:
 * a colour dot (when the property has one) followed by
 * "{company name} -- {property name}".
 *
 * Long names are ellipsised, unless the surrounding PaginatedSelect has its
 * "show full names" toggle switched on — then they wrap and show in full.
 */
export const PropertyOptionLabel = ({ color, text }) => {
    const expanded = useContext(LabelExpandContext);

    return (
        <div
            style={{
                display: 'flex',
                alignItems: expanded ? 'flex-start' : 'center',
                gap: 8,
            }}
        >
            {color ? (
                <span
                    style={{
                        display: 'inline-block',
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        backgroundColor: color,
                        flexShrink: 0,
                        marginTop: expanded ? 4 : 0,
                        border: '1px solid rgba(0,0,0,0.18)',
                    }}
                />
            ) : null}
            <span
                title={text}
                style={
                    expanded
                        ? {
                            flex: 1,
                            minWidth: 0,
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                        }
                        : {
                            flex: 1,
                            minWidth: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }
                }
            >
                {text}
            </span>
        </div>
    );
};

/**
 * Turns a raw property object from the API into a dropdown option.
 * `searchText` is the plain-text version of the label (label itself is a node),
 * used for in-memory filtering and by the select to render the picked value.
 */
export const buildPropertyOption = (p) => {
    if (!p) return null;
    const textLabel =
        p.company && p.company.length > 0
            ? `${p.company[0].name} -- ${p.name}`
            : p.name;

    return {
        label: <PropertyOptionLabel color={p.color} text={textLabel} />,
        value: p.id,
        searchText: p.code ? `${textLabel} ${p.code}` : textLabel,
    };
};

export default PropertyOptionLabel;
